// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });
const AUSTRALIAN_STATE_PATTERN = '(NSW|VIC|QLD|SA|WA|TAS|ACT|NT)';
const DEFAULT_LOCATION_LABEL = 'Sydney, NSW';

/**
 * Keep the browse card location intentionally broad. Studio listings may save a full address,
 * but search cards should only expose suburb/state before a booking is accepted.
 */
const getPublicLocationLabel = address => {
  if (!address || typeof address !== 'string') {
    return null;
  }

  const addressParts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .filter(part => part.toLowerCase() !== 'australia');

  if (addressParts.length === 0) {
    return null;
  }

  const localityPart = addressParts[addressParts.length - 1];
  const localityMatch = localityPart.match(
    new RegExp(`^(.+?)\\s+${AUSTRALIAN_STATE_PATTERN}(?:\\s+\\d{4})?$`, 'i')
  );

  if (localityMatch) {
    return `${localityMatch[1].trim()}, ${localityMatch[2].toUpperCase()}`;
  }

  const stateOnlyMatch = localityPart.match(
    new RegExp(`^${AUSTRALIAN_STATE_PATTERN}(?:\\s+\\d{4})?$`, 'i')
  );
  if (stateOnlyMatch && addressParts.length > 1) {
    const suburb = addressParts[addressParts.length - 2];
    return suburb && !/\d/.test(suburb)
      ? `${suburb}, ${stateOnlyMatch[1].toUpperCase()}`
      : null;
  }

  // A plain suburb name is safe to show. Avoid returning strings containing street numbers.
  return addressParts.length === 1 && !/\d/.test(localityPart) ? localityPart : null;
};

/**
 * Older Hotline Glam listings may not yet have serviceLocationType saved because that field was
 * added later. Infer it from legacy location-pricing data and location fields when possible so
 * Mobile/Studio still appears on browse cards.
 */
const resolveServiceLocationType = publicData => {
  const directType = publicData?.serviceLocationType;
  if (['mobile', 'studio', 'both'].includes(directType)) {
    return directType;
  }

  const locationPricingEntries = [
    ...(publicData?.priceVariants || []),
    ...(publicData?.travelFees || []),
  ];
  const locationPricingNames = locationPricingEntries
    .map(entry => entry?.name)
    .filter(name => typeof name === 'string');

  const hasLegacyMobilePricing = locationPricingNames.some(name => /^Mobile:\s*/i.test(name));
  const hasLegacyStudioPricing = locationPricingNames.some(name => name.trim() === 'Studio');
  const hasMobileArea = !!publicData?.mobileServiceArea?.trim?.();
  const hasStudioDetails = !!publicData?.location?.building?.trim?.();

  const hasMobile = hasLegacyMobilePricing || hasMobileArea;
  const hasStudio = hasLegacyStudioPricing || hasStudioDetails;

  if (hasMobile && hasStudio) return 'both';
  if (hasMobile) return 'mobile';
  if (hasStudio) return 'studio';
  return null;
};

const getServiceLocationItems = (serviceLocationType, intl) => {
  const mobile = {
    key: 'mobile',
    label: intl.formatMessage({ id: 'ListingCard.mobile', defaultMessage: 'Mobile' }),
  };
  const studio = {
    key: 'studio',
    label: intl.formatMessage({ id: 'ListingCard.studio', defaultMessage: 'Studio' }),
  };

  if (serviceLocationType === 'mobile') return [mobile];
  if (serviceLocationType === 'studio') return [studio];
  if (serviceLocationType === 'both') return [mobile, studio];
  return [];
};

const LocationMetaIcon = () => (
  <svg
    className={css.metaIcon}
    data-testid="location-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z" />
    <circle cx="12" cy="10" r="2.2" />
  </svg>
);

const MobileMetaIcon = () => (
  <svg
    className={css.metaIcon}
    data-testid="mobile-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M4 14.5 5.6 9h12.8l1.6 5.5" />
    <path d="M3.5 14.5h17v4h-2v1.5h-2v-1.5h-9V20h-2v-1.5h-2v-4Z" />
    <circle cx="7.5" cy="16.5" r="1" />
    <circle cx="16.5" cy="16.5" r="1" />
  </svg>
);

const StudioMetaIcon = () => (
  <svg
    className={css.metaIcon}
    data-testid="studio-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="m4 11 8-6 8 6" />
    <path d="M6.5 10.5V20h11v-9.5" />
    <path d="M10 20v-5h4v5" />
  </svg>
);

const ServiceMetaIcon = ({ type }) =>
  type === 'studio' ? <StudioMetaIcon /> : <MobileMetaIcon />;

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showLocationInfo = false,
    horizontal = false,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className, {
    [css.horizontal]: horizontal,
  });

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle, location } = publicData || {};
  const locationLabel = showLocationInfo
    ? getPublicLocationLabel(location?.address) || DEFAULT_LOCATION_LABEL
    : null;
  const resolvedServiceLocationType = resolveServiceLocationType(publicData);
  const serviceLocationItems = showLocationInfo
    ? getServiceLocationItems(resolvedServiceLocationType, intl)
    : [];

  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
          {showLocationInfo ? (
            <div className={classNames(css.searchMeta, { [css.lightText]: darkMode })}>
              <span className={css.locationMeta}>
                <LocationMetaIcon />
                <span>{locationLabel}</span>
              </span>
              {serviceLocationItems.map(item => (
                <span key={item.key} className={css.serviceLocationMeta}>
                  <ServiceMetaIcon type={item.key} />
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
